'use client';
import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { Calculator, CheckCircle, DollarSign, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const PLATFORM_COST_OPTIONS = [
  { label: '$500K', value: 500000 },
  { label: '$1M', value: 1000000 },
  { label: '$2M', value: 2000000 },
  { label: '$3M', value: 3000000 },
];

const ANNUAL_GAIN_OPTIONS = [
  { label: '$4.5M/yr (+Conservative)', value: 4500000 },
  { label: '$9.1M/yr (+Moderate)', value: 9100000 },
  { label: '$14.2M/yr (+Aggressive)', value: 14200000 },
  { label: '$19.3M/yr (+Full Gap)', value: 19260000 },
];

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 shadow-xl text-xs"
      style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
      <p className="font-bold mb-1" style={{ color: 'var(--dark-text)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.value >= 0 ? 'Net Gain' : 'Net Cost'}: <span className="font-bold">${Math.abs(p.value / 1_000_000).toFixed(2)}M</span>
        </p>
      ))}
    </div>
  );
}

export default function BreakEvenCalculator() {
  const [platformCost, setPlatformCost] = useState(2000000);
  const [annualGain, setAnnualGain] = useState(9100000);
  const [maintenancePct, setMaintenancePct] = useState(15);

  const annualMaintenance = platformCost * (maintenancePct / 100);
  const netAnnualGain = annualGain - annualMaintenance;
  const breakEvenMonths = netAnnualGain > 0 ? (platformCost / netAnnualGain) * 12 : Infinity;
  const breakEvenYears = breakEvenMonths / 12;
  const roi5yr = ((netAnnualGain * 5 - platformCost) / platformCost) * 100;

  // Build 5-year cumulative chart data
  const chartData = Array.from({ length: 6 }, (_, i) => {
    const cumGain = netAnnualGain * i;
    const net = cumGain - platformCost;
    return {
      year: i === 0 ? 'Deploy' : `Year ${i}`,
      net,
      cumGain,
    };
  });

  const breakEvenLabel = breakEvenYears <= 5
    ? `${breakEvenMonths.toFixed(1)} months`
    : 'Beyond 5 years';

  const breakEvenColor = breakEvenYears <= 1 ? '#4ade80' : breakEvenYears <= 2 ? '#fbbf24' : '#f87171';

  return (
    <section className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--dark-border)', background: 'var(--dark-card)' }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4" style={{
        borderBottom: '1px solid var(--dark-border)',
        background: 'linear-gradient(135deg, rgba(74,222,128,0.08) 0%, rgba(96,165,250,0.05) 100%)'
      }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(74,222,128,0.15)' }}>
            <Calculator size={16} style={{ color: '#4ade80' }} />
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#4ade80' }}>Break-Even Calculator</span>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Interactive ROI model — adjust inputs to match your proposal</p>
          </div>
        </div>
        <h2 className="text-lg font-bold mt-2" style={{ color: 'var(--dark-text)' }}>
          How Fast Does LRA Nexus Pay for Itself?
        </h2>
        <p className="text-xs mt-0.5 max-w-2xl" style={{ color: 'var(--dark-muted)' }}>
          At $2M platform cost with moderate revenue recovery, the system pays for itself in under 3 months of Year 1 gains. Adjust the sliders to model your specific proposal.
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="space-y-5">
            {/* Platform cost */}
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--dark-text)' }}>
                Platform Implementation Cost
              </label>
              <div className="grid grid-cols-4 gap-2">
                {PLATFORM_COST_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPlatformCost(opt.value)}
                    className="py-2 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: platformCost === opt.value ? 'rgba(74,222,128,0.15)' : 'var(--dark-elevated)',
                      color: platformCost === opt.value ? '#4ade80' : 'var(--dark-muted)',
                      border: `1px solid ${platformCost === opt.value ? 'rgba(74,222,128,0.3)' : 'var(--dark-border)'}`,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Annual revenue gain */}
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--dark-text)' }}>
                Annual Revenue Recovery (above baseline)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {ANNUAL_GAIN_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setAnnualGain(opt.value)}
                    className="py-2 px-3 rounded-lg text-xs font-semibold transition-all text-left"
                    style={{
                      background: annualGain === opt.value ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                      color: annualGain === opt.value ? '#d4a017' : 'var(--dark-muted)',
                      border: `1px solid ${annualGain === opt.value ? 'rgba(212,160,23,0.3)' : 'var(--dark-border)'}`,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Maintenance slider */}
            <div>
              <label className="text-xs font-semibold mb-2 flex justify-between" style={{ color: 'var(--dark-text)' }}>
                <span>Annual Maintenance Cost</span>
                <span style={{ color: '#60a5fa' }}>{maintenancePct}% of platform cost (${(annualMaintenance / 1000).toFixed(0)}K/yr)</span>
              </label>
              <input
                type="range"
                min={5}
                max={30}
                step={5}
                value={maintenancePct}
                onChange={e => setMaintenancePct(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: '#60a5fa' }}
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
                <span>5%</span><span>10%</span><span>15%</span><span>20%</span><span>25%</span><span>30%</span>
              </div>
            </div>

            {/* Result cards */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: 'Break-Even Point',
                  value: breakEvenLabel,
                  color: breakEvenColor,
                  icon: Zap,
                  bg: `${breakEvenColor}12`,
                  border: `${breakEvenColor}25`,
                },
                {
                  label: '5-Year ROI',
                  value: `${roi5yr > 0 ? '+' : ''}${roi5yr.toFixed(0)}%`,
                  color: roi5yr > 0 ? '#4ade80' : '#f87171',
                  icon: DollarSign,
                  bg: roi5yr > 0 ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
                  border: roi5yr > 0 ? 'rgba(74,222,128,0.2)' : 'rgba(239,68,68,0.2)',
                },
              ].map(({ label, value, color, icon: Icon, bg, border }) => (
                <div key={label} className="rounded-xl px-4 py-4" style={{ background: bg, border: `1px solid ${border}` }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={13} style={{ color }} />
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{label}</span>
                  </div>
                  <p className="text-xl font-black" style={{ color, letterSpacing: '-0.02em' }}>{value}</p>
                </div>
              ))}
            </div>

            {/* Net gain summary */}
            <div className="rounded-xl px-4 py-3" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              {[
                { label: 'Platform Cost', value: `-$${(platformCost / 1_000_000).toFixed(1)}M`, color: '#f87171' },
                { label: 'Annual Maintenance (5yr)', value: `-$${(annualMaintenance * 5 / 1_000_000).toFixed(2)}M`, color: '#fb923c' },
                { label: 'Revenue Recovered (5yr)', value: `+$${(annualGain * 5 / 1_000_000).toFixed(1)}M`, color: '#4ade80' },
                { label: 'Net 5-Year Gain', value: `$${((annualGain * 5 - platformCost - annualMaintenance * 5) / 1_000_000).toFixed(2)}M`, color: '#fbbf24', bold: true },
              ].map(({ label, value, color, bold }) => (
                <div key={label} className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--dark-border)' }}>
                  <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{label}</span>
                  <span className={`text-xs ${bold ? 'text-sm font-black' : 'font-semibold'}`} style={{ color }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div>
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Cumulative Net Position (5-Year)</p>
            <div className="rounded-xl p-4" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="year" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false}
                    tickFormatter={v => `$${(v / 1_000_000).toFixed(1)}M`} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeWidth={1.5} />
                  <Bar dataKey="net" name="Net Position" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.net >= 0 ? '#4ade80' : '#f87171'} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Checklist */}
            <div className="mt-4 space-y-2">
              {[
                'Platform cost recovered within Year 1 at moderate scenario',
                'Every $1 invested returns $4–$10 over 5 years',
                'Annual maintenance covered by <1 month of new revenue',
                'IMF recommends property tax at 0.5% GDP — this closes that gap',
              ].map(item => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle size={13} className="mt-0.5 shrink-0" style={{ color: '#4ade80' }} />
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
